// useDocumentTitle - what the browser tab says the page is.
//
// A dashboard is a tab that stays open for hours beside a dozen others, and
// "Foresee Sandbox" on every one of them is the same as no title at all: the
// reader picks the right tab by its favicon and then by its text, and until
// now the text never changed. It also decides what a bookmark and a history
// entry are called, which is where a static title does the most damage.
//
// One owner per page. App renders <RouteTitle> ahead of <Routes>, which gives
// every route a title derived from its path; a page with something better to
// say (the report names its sample, the console names its components) calls
// this hook and wins, because React flushes a child's effects after its
// earlier siblings'. Nothing restores a title on unmount - RouteTitle rewrites
// it on the next path change, and a cleanup racing that would flash the brand.

import { useEffect } from 'react';

/** The product name, the suffix on every title. */
export const APP_NAME = 'Foresee Sandbox';

/** `<label> · Foresee Sandbox`, or the bare product name when there is no
 *  label. Exported because RouteTitle writes the title on a different trigger
 *  than this hook does and must not spell the format a second time. */
export function documentTitle(label: string | null): string {
  return label ? `${label} · ${APP_NAME}` : APP_NAME;
}

/**
 * Sets `document.title` to `<label> · Foresee Sandbox` for as long as this
 * component is mounted, or to the bare product name when `label` is null.
 *
 * Pass null while the name is still loading rather than a placeholder: the
 * brand alone is honest, "Loading…" in a tab strip is not.
 */
export function useDocumentTitle(label: string | null): void {
  useEffect(() => {
    document.title = documentTitle(label);
  }, [label]);
}
